import type { Styles } from '@adornis/ass/style.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { XContainerFlexExtension } from '@adornis/buildify/client/extensions/ContainerFlexExtension.js';
import { EditExtensionData } from '@adornis/buildify/client/extensions/EditExtensionData.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import type { Base } from '@adornis/buildify/db/Base.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { BuildifyCampusContainerFlexExtension } from '@adornis/digitale-helden-shared/client/theme/buildify/extensions/container/flex/BuildifyCampusContainerFlexExtension.js';
import { LASFile } from '@adornis/digitale-helden-shared/features/mediaverwaltung/db/LASFile.js';
import { consume } from '@lit/context';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { navElementContext } from '../../client/contexts.js';
import { BaseFullsizeContainer } from '../db/BaseFullsizeContainer.js';

export const MarketingFullsizeContainerExtension: BuildifyExtension<BaseFullsizeContainer> = {
  class: BaseFullsizeContainer,
  toolbar: {
    icon: 'expand-wide',
    text: {
      default: 'Fullscreen',
    },
  },
  group: 'Layout',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    console.log('render fullsize', dropzoneBefore, dropzoneAfter);
    return html`
      <marketing-fullsize-container
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-fullsize-container>
    `;
  },
  editor({ content, contentController, controllerBaseKeyPath, host, globalSettings, extensions }) {
    return html`
      <d-flex space="sm">
        <x-buildify-file-selection
          image
          ${contentController.field(...controllerBaseKeyPath, 'backgroundImageID')}
        ></x-buildify-file-selection>

        ${content.backgroundImageID
          ? html`
              <d-dropdown-selection
                placeholder="Hintergrund Repeat"
                clearable
                .selectables=${['no-repeat', 'repeat']}
                ${contentController.field(...controllerBaseKeyPath, 'backgroundRepeat')}
              ></d-dropdown-selection>
              <d-dropdown-selection
                placeholder="Hintergrund Größe"
                clearable
                .selectables=${['contain', 'cover']}
                ${contentController.field(...controllerBaseKeyPath, 'backgroundSize')}
              ></d-dropdown-selection>
              <d-dropdown-selection
                placeholder="Hintergrund Position"
                clearable
                .selectables=${['bottom', 'center', 'top', 'left', 'right']}
                ${contentController.field(...controllerBaseKeyPath, 'backgroundPosition')}
              ></d-dropdown-selection>
            `
          : nothing}
        ${EditExtensionData({
          host,
          content: {
            value: content,
            path: controllerBaseKeyPath,
            controller: contentController,
            extension: BuildifyCampusContainerFlexExtension({}),
          },
          globalSettings,
          extensions,
        })}
      </d-flex>
    `;
  },
};

@customElement('marketing-fullsize-container')
export class MFullsizeContainer extends XContainerFlexExtension {
  @consume({ context: navElementContext })
  @state()
  private readonly _navElement;

  @state() private readonly _windowSize = new WindowSizeController(this);

  override _content(content: BaseFullsizeContainer) {
    if (this._contentFilteredSorted?.length === 0) {
      return html` <x-buildify-dropzone .index=${0} .parentID=${content._id}></x-buildify-dropzone> `;
    }

    const contents = repeat(this._contentFilteredSorted ?? [], (content: Base, index) =>
      this.__item(content, content.index, index),
    );

    return contents;
  }

  override styles() {
    const content = this._convertedContent as Maybe<BaseFullsizeContainer>;
    if (!content) return super.styles();
    const height = (this._windowSize.height ?? 0) - (this._navElement?.value?.getClientRects()?.[0]?.height ?? 0);

    return [
      ...super.styles(),
      {
        ':host': {
          boxSizing: 'border-box',
          minHeight: `${height}px`,
          maxHeight: `${height}px`,
          background: `url('${LASFile.getServeLink(content.backgroundImageID ?? '')}')`,
          backgroundRepeat: content.backgroundRepeat ?? 'no-repeat',
          backgroundSize: content.backgroundSize ?? 'cover',
          backgroundPosition: content.backgroundPosition ?? 'bottom',
        },
      },
    ] as Styles[];
  }
}
